import {CreateIssueButton} from '@github-ui/issue-create/CreateIssueButton'
import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {type To, useLocation} from '@github-ui/react-router'
import {useNavigate} from '@github-ui/use-navigate'
import {ArrowLeftIcon, SidebarExpandIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button, IconButton, Truncate} from '@primer/react'
import {useCallback} from 'react'
import {graphql, useFragment} from 'react-relay'

import {BUTTON_LABELS} from '../../../constants/buttons'
import {LABELS} from '../../../constants/labels'
import {TEST_IDS} from '../../../constants/test-ids'
import {useNavigationContext} from '../../../contexts/NavigationContext'
import {useQueryContext} from '../../../contexts/QueryContext'
import {useAppNavigate} from '../../../hooks/use-app-navigate'
import type {AppPayload} from '../../../types/app-payload'
import {getCurrentRepoIssuesUrl, isNewIssuePath} from '../../../utils/urls'
import type {HeaderCurrentViewFragment$key} from './__generated__/HeaderCurrentViewFragment.graphql'
import styles from './Header.module.css'
import {HeaderContent} from './HeaderContent'

type HeaderProps = {
  onCollapse?: () => void
  currentViewKey: HeaderCurrentViewFragment$key
}

export function Header({currentViewKey, onCollapse}: HeaderProps) {
  const {pathname} = useLocation()
  const isIssueCreatePage = isNewIssuePath(pathname)
  const {navigateToRoot, navigateToUrl} = useAppNavigate()
  const {isEditing, canEditView} = useQueryContext()

  const currentViewData = useFragment<HeaderCurrentViewFragment$key>(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment HeaderCurrentViewFragment on Shortcutable {
        id
        name
        query
        ...HeaderContentCurrentViewFragment
      }
    `,
    currentViewKey,
  )

  const {id: viewId, name: viewName, query: viewQuery} = currentViewData

  const {openNavigation} = useNavigationContext()
  const {scoped_repository, current_user_settings} = useAppPayload<AppPayload>()
  const {activeSearchQuery} = useQueryContext()

  const backFunction = useCallback(() => {
    if (viewId === VIEW_IDS.repository && activeSearchQuery !== viewQuery) {
      const url = getCurrentRepoIssuesUrl({query: activeSearchQuery})
      navigateToUrl(url)
    } else {
      navigateToRoot(viewId, viewQuery)
    }
  }, [activeSearchQuery, navigateToRoot, navigateToUrl, viewId, viewQuery])

  const navigate = useNavigate()

  const fullScreenNavigate = useCallback(
    (url: To) => {
      navigate(url, {reloadDocument: true})
    },
    [navigate],
  )

  return (
    <div data-testid={TEST_IDS.listHeader}>
      {isIssueCreatePage ? (
        <div className={styles.HeaderCreateIssueContainer}>
          <div className={styles.leftSideContent}>
            {/* we are using an expand icon as primer's `SidebarCollapseIcon` expects the sidebar to be on the right */}
            {onCollapse && (
              <IconButton
                variant="invisible"
                icon={SidebarExpandIcon}
                onClick={onCollapse}
                aria-label="Collapse"
                keybindingHint="Mod+B"
                tooltipDirection="e"
              />
            )}
            <Button
              variant="invisible"
              onClick={backFunction}
              leadingVisual={ArrowLeftIcon}
              size="small"
              title={BUTTON_LABELS.returnToList}
              className={styles.Button}
            >
              <Truncate id="view-title" title={viewName} className={styles.Truncate}>
                {viewName}
              </Truncate>
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.HeaderListContainer}>
          {!scoped_repository && (
            <div className={styles.mobileNavigationContainer}>
              <Button
                variant="invisible"
                size="small"
                onClick={openNavigation}
                trailingVisual={TriangleDownIcon}
                className={styles.allViewsButton}
              >
                {LABELS.allViews}
              </Button>
            </div>
          )}
          <HeaderContent readOnly={!canEditView} currentViewKey={currentViewData} />
          {!isEditing && (
            <div className={styles.actionButtonsContainer}>
              {!scoped_repository && (
                <CreateIssueButton
                  label={BUTTON_LABELS.newIssue}
                  navigate={fullScreenNavigate}
                  optionConfig={{
                    showRepositoryPicker: scoped_repository === null,
                    emojiSkinTonePreference: current_user_settings?.preferred_emoji_skin_tone,
                    showFullScreenButton: true,
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

try{ Header.displayName ||= 'Header' } catch {}